import {
  DocumentDrawing,
  DocumentDrawingTarget,
  DocumentRegion,
  useLibraDocument,
} from 'features/libra/document/LibraDocumentContext'
import { useRolodexEntries } from 'features/rolodex'
import isEqual from 'lodash/isEqual'
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { RolodexEntryType } from 'types'
import { v4 } from 'uuid'

export type DocumentDrawingCommand = {
  token: string
  documentId: string
  resetKey?: number
  selectedRegionId?: string
  replacementRegionId?: string
}

type DrawingIntent =
  | { type: 'create' }
  | { type: 'redraw'; regionId: string; imageId: string | undefined }

type DrawingBinding =
  | { state: 'waiting' }
  | { state: 'bound'; sourceKey: string; element: HTMLElement }

type ActiveDrawingCommand<Context> = Omit<
  DocumentDrawingCommand,
  'replacementRegionId'
> & {
  intent: DrawingIntent
  binding: DrawingBinding
  interactionRevision?: number | string
  context?: Context
}

type NewDrawingCommand<Context> = Omit<ActiveDrawingCommand<Context>, 'binding'>

const sourceKey = ({ documentId, url }: DocumentDrawingTarget) =>
  JSON.stringify([documentId, url])

const describeCommand = <Context>(
  active: ActiveDrawingCommand<Context> | undefined
): DocumentDrawingCommand | undefined =>
  active && {
    token: active.token,
    documentId: active.documentId,
    resetKey: active.resetKey,
    selectedRegionId: active.selectedRegionId,
    ...(active.intent.type === 'redraw'
      ? { replacementRegionId: active.intent.regionId }
      : {}),
  }

export const useDocumentDrawing = <Context = undefined>({
  regions,
  unitId,
  disabled,
  isLoading,
  onDraw,
  onImageNaturalSize,
  selectedRegionId,
  onSelectRegion,
  onUpdateRegion,
  onRemoveRegion,
  referenceDocumentIds,
  interactionRevision,
  drawingResetKey,
}: {
  regions: readonly DocumentRegion[]
  unitId: string | undefined
  disabled: boolean
  isLoading: boolean
  onDraw: (
    drawing: DocumentDrawing,
    regionId?: string,
    context?: Context
  ) => string | undefined
  onImageNaturalSize?: (
    size: { documentId: string; width: number; height: number } | undefined
  ) => void
  selectedRegionId?: string
  onSelectRegion?: (regionId: string | undefined) => void
  onUpdateRegion?: (
    regionId: string,
    bounds: DocumentDrawing['drawing_bounds']
  ) => void
  onRemoveRegion?: (regionId: string) => void
  /** Reference images with zones but no remaining rectangles. */
  referenceDocumentIds?: readonly string[]
  /** Host revision for invalidating commands after structural target changes. */
  interactionRevision?: number | string
  drawingResetKey?: number
}) => {
  const {
    selectedDocumentId,
    setSelectedDocumentId,
    drawingHost,
    setDrawingHost,
    unregisterDrawingHost,
    drawingTarget,
  } = useLibraDocument()
  const hostId = useRef(Symbol('document-drawing-host'))
  const ownsRoute = drawingHost?.owner === hostId.current
  const { reports, forceRefresh } = useRolodexEntries(
    useMemo(
      () =>
        unitId
          ? { type: RolodexEntryType.REPORT, tags: { unit: unitId } }
          : undefined,
      [unitId]
    ),
    { swrOptions: { revalidateOnFocus: false, compare: isEqual } }
  )
  const selectedDocument = reports.find(
    (report) => report.id === selectedDocumentId
  )
  const visibleRegions = useMemo(
    () =>
      regions.filter(
        (region) => region.zone_reference_image_id === selectedDocumentId
      ),
    [regions, selectedDocumentId]
  )
  const categories = selectedDocument?.raw.data.categories
  const isReference =
    visibleRegions.length > 0 ||
    Boolean(
      selectedDocumentId && referenceDocumentIds?.includes(selectedDocumentId)
    ) ||
    (Array.isArray(categories) && categories.includes('Zone Reference Images'))
  const routeDocumentId = isReference
    ? (selectedDocumentId ?? undefined)
    : undefined
  const [activeCommand, setActiveCommand] =
    useState<ActiveDrawingCommand<Context>>()
  const activeCommandRef = useRef<ActiveDrawingCommand<Context>>()
  const mounted = useRef(false)
  const transitionCommand = useCallback(
    (next?: ActiveDrawingCommand<Context>) => {
      activeCommandRef.current = next
      if (mounted.current) setActiveCommand(next)
    },
    []
  )
  const command = useMemo(() => describeCommand(activeCommand), [activeCommand])
  const [cropDocumentId, setCropDocumentId] = useState<string>()
  const interactionKey = useMemo(
    () => ({ selectedDocumentId, cropDocumentId, disabled, isLoading }),
    [selectedDocumentId, cropDocumentId, disabled, isLoading]
  )
  const canDraw = Boolean(
    selectedDocumentId &&
    isReference &&
    !disabled &&
    !cropDocumentId &&
    ownsRoute &&
    drawingHost?.documentId === selectedDocumentId &&
    drawingTarget?.documentId === selectedDocumentId &&
    Boolean(drawingTarget.url)
  )
  const current = {
    ...interactionKey,
    selectedDocumentId,
    selectedRegionId,
    interactionKey,
    regions,
    referenceDocumentIds,
    drawingResetKey,
    interactionRevision,
    canDraw,
    ownsRoute,
    drawingTarget,
    onDraw,
    onSelectRegion,
    onUpdateRegion,
    onRemoveRegion,
  }
  const currentRef = useRef(current)
  currentRef.current = current

  const isCommandValid = useCallback(
    (active: ActiveDrawingCommand<Context>, latest = currentRef.current) => {
      if (
        !latest.ownsRoute ||
        latest.disabled ||
        latest.selectedDocumentId !== active.documentId ||
        latest.interactionRevision !== active.interactionRevision ||
        latest.selectedRegionId !== active.selectedRegionId ||
        latest.drawingResetKey !== active.resetKey ||
        latest.cropDocumentId === active.documentId
      )
        return false

      if (active.intent.type === 'create') return true
      const intent = active.intent

      return latest.regions.some(
        (region) =>
          region.id === intent.regionId &&
          !region.readOnly &&
          region.zone_reference_image_id === intent.imageId &&
          (region.redrawTargetDocumentId ?? region.zone_reference_image_id) ===
            active.documentId
      )
    },
    []
  )
  const isDrawing = Boolean(
    canDraw &&
    activeCommand &&
    isCommandValid(activeCommand, current) &&
    activeCommand.binding.state === 'bound'
  )

  const canEditRegion = useCallback(
    (regionId: string) => {
      const latest = currentRef.current
      return (
        latest.ownsRoute &&
        latest.interactionKey === interactionKey &&
        latest.drawingResetKey === drawingResetKey &&
        !latest.disabled &&
        latest.cropDocumentId !== latest.selectedDocumentId &&
        latest.regions.some(
          (region) =>
            region.id === regionId &&
            !region.readOnly &&
            region.zone_reference_image_id === latest.selectedDocumentId
        )
      )
    },
    [drawingResetKey, interactionKey]
  )

  useLayoutEffect(() => {
    const owner = hostId.current
    setDrawingHost(owner, undefined, true)
    return () => unregisterDrawingHost(owner)
  }, [setDrawingHost, unregisterDrawingHost])

  useLayoutEffect(() => {
    setDrawingHost(hostId.current, routeDocumentId)
  }, [routeDocumentId, setDrawingHost])

  useLayoutEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      transitionCommand(undefined)
    }
  }, [transitionCommand])

  const cancelDrawing = useCallback(
    (token?: string) => {
      const active = activeCommandRef.current
      if (!active || (token && active.token !== token)) return false
      transitionCommand(undefined)
      return true
    },
    [transitionCommand]
  )

  const armDrawing = (next?: NewDrawingCommand<Context>) => {
    const target = currentRef.current.drawingTarget
    transitionCommand(
      next
        ? {
            ...next,
            interactionRevision: currentRef.current.interactionRevision,
            binding:
              target?.documentId === next.documentId && target.url
                ? {
                    state: 'bound',
                    sourceKey: sourceKey(target),
                    element: target.element,
                  }
                : { state: 'waiting' },
          }
        : undefined
    )
  }

  const finishDrawing = useCallback(
    (
      bounds: DocumentDrawing['drawing_bounds'],
      token: string
    ): string | undefined => {
      const latest = currentRef.current
      const active = activeCommandRef.current
      const target = latest.drawingTarget
      if (
        !mounted.current ||
        !latest.canDraw ||
        !active ||
        active.token !== token ||
        !isCommandValid(active, latest) ||
        active.binding.state !== 'bound' ||
        !target ||
        target.documentId !== active.documentId ||
        active.binding.element !== target.element ||
        active.binding.sourceKey !== sourceKey(target)
      )
        return undefined
      const drawing = {
        drawing_bounds: bounds,
        zone_reference_image_id: active.documentId,
      }
      const regionId =
        active.intent.type === 'redraw' ? active.intent.regionId : undefined
      const acceptedId =
        active.context === undefined
          ? latest.onDraw(drawing, regionId)
          : latest.onDraw(drawing, regionId, active.context)
      if (acceptedId !== undefined) cancelDrawing(token)
      return acceptedId
    },
    [cancelDrawing, isCommandValid]
  )

  useLayoutEffect(() => {
    const active = activeCommandRef.current
    if (active && !isCommandValid(active)) cancelDrawing(active.token)
    if (disabled || cropDocumentId !== selectedDocumentId)
      setCropDocumentId(undefined)
    if (activeCommandRef.current !== active) return
    if (!active) return
    if (!drawingTarget) {
      cancelDrawing(active.token)
      return
    }
    if (drawingTarget.documentId !== active.documentId) {
      if (active.binding.state === 'bound') cancelDrawing(active.token)
      return
    }
    if (!drawingTarget.url) {
      if (active.binding.state === 'bound') cancelDrawing(active.token)
      return
    }
    const identity = sourceKey(drawingTarget)
    if (active.binding.state === 'waiting') {
      transitionCommand({
        ...active,
        binding: {
          state: 'bound',
          sourceKey: identity,
          element: drawingTarget.element,
        },
      })
      return
    }
    if (
      active.binding.sourceKey !== identity ||
      active.binding.element !== drawingTarget.element
    )
      cancelDrawing(active.token)
  }, [
    ownsRoute,
    disabled,
    selectedDocumentId,
    selectedRegionId,
    interactionRevision,
    drawingResetKey,
    cropDocumentId,
    regions,
    drawingTarget,
    cancelDrawing,
    isCommandValid,
    transitionCommand,
  ])

  const selectRegion = useCallback(
    (regionId: string | undefined) => {
      const latest = currentRef.current
      if (
        !mounted.current ||
        !latest.ownsRoute ||
        latest.isLoading ||
        latest.interactionKey !== interactionKey ||
        (regionId &&
          !latest.regions.some(
            (region) =>
              region.id === regionId &&
              region.zone_reference_image_id === latest.selectedDocumentId
          ))
      )
        return
      latest.onSelectRegion?.(regionId)
    },
    [interactionKey]
  )

  const updateRegion = useCallback(
    (regionId: string, bounds: DocumentDrawing['drawing_bounds']) => {
      const latest = currentRef.current
      if (
        !mounted.current ||
        !latest.onUpdateRegion ||
        !canEditRegion(regionId)
      )
        return
      latest.onUpdateRegion(regionId, bounds)
    },
    [canEditRegion]
  )

  const removeRegion = useCallback(
    (regionId: string) => {
      const latest = currentRef.current
      if (
        !mounted.current ||
        !latest.onRemoveRegion ||
        !canEditRegion(regionId)
      )
        return
      latest.onRemoveRegion(regionId)
    },
    [canEditRegion]
  )

  const startDrawingForSelection = (selectedRegionId?: string) => {
    const latest = currentRef.current
    const documentId = latest.selectedDocumentId
    if (!latest.ownsRoute || !latest.canDraw || !documentId) return
    const next: NewDrawingCommand<Context> = {
      token: v4(),
      documentId,
      resetKey: latest.drawingResetKey,
      selectedRegionId,
      intent: { type: 'create' },
    }
    armDrawing(next)
  }

  const startDrawing = () =>
    startDrawingForSelection(currentRef.current.selectedRegionId)

  const startDrawingForDocument = (
    documentId: string,
    context: Context | undefined,
    selectedRegionId: string | undefined
  ) => {
    const latest = currentRef.current
    if (
      !latest.ownsRoute ||
      latest.disabled ||
      (!latest.referenceDocumentIds?.includes(documentId) &&
        !latest.regions.some(
          (region) => region.zone_reference_image_id === documentId
        )) ||
      latest.cropDocumentId === documentId
    )
      return
    if (latest.cropDocumentId) {
      currentRef.current.cropDocumentId = undefined
      setCropDocumentId(undefined)
    }
    setSelectedDocumentId(documentId)
    armDrawing({
      token: v4(),
      documentId,
      resetKey: latest.drawingResetKey,
      selectedRegionId,
      intent: { type: 'create' },
      context,
    })
  }

  const showRegion = (region: DocumentRegion, redraw = false) => {
    const latest = currentRef.current
    const currentRegion = latest.regions.find(
      (candidate) => candidate.id === region.id
    )
    if (!currentRegion) return
    const targetDocumentId = redraw
      ? (currentRegion.redrawTargetDocumentId ??
        currentRegion.zone_reference_image_id)
      : currentRegion.zone_reference_image_id
    if (!targetDocumentId) return
    if (latest.cropDocumentId && latest.cropDocumentId !== targetDocumentId) {
      currentRef.current.cropDocumentId = undefined
      setCropDocumentId(undefined)
    }
    latest.onSelectRegion?.(region.id)
    setSelectedDocumentId(targetDocumentId)
    const next: NewDrawingCommand<Context> | undefined =
      redraw &&
      !currentRegion.readOnly &&
      !latest.disabled &&
      latest.cropDocumentId !== targetDocumentId
        ? {
            token: v4(),
            documentId: targetDocumentId,
            resetKey: latest.drawingResetKey,
            selectedRegionId: latest.onSelectRegion
              ? currentRegion.id
              : latest.selectedRegionId,
            intent: {
              type: 'redraw',
              regionId: currentRegion.id,
              imageId: currentRegion.zone_reference_image_id,
            },
          }
        : undefined
    armDrawing(next)
  }

  const startCrop = () => {
    const latest = currentRef.current
    if (!latest.selectedDocumentId || latest.disabled) return
    cancelDrawing()
    currentRef.current.cropDocumentId = latest.selectedDocumentId
    setCropDocumentId(latest.selectedDocumentId)
  }
  const finishCrop = (id: string) => {
    currentRef.current.cropDocumentId = undefined
    setCropDocumentId(undefined)
    setSelectedDocumentId(id)
    void forceRefresh()
  }

  const targetMatchesSource = Boolean(
    ownsRoute &&
    isReference &&
    drawingHost?.documentId === selectedDocumentId &&
    drawingTarget &&
    drawingTarget.documentId === selectedDocumentId &&
    cropDocumentId !== selectedDocumentId
  )
  const viewProps = {
    target: targetMatchesSource ? drawingTarget : undefined,
    liveZones: visibleRegions,
    readOnly: disabled,
    isLoading,
    drawingCommand: isDrawing ? command : undefined,
    onZoneDrawn:
      isDrawing && activeCommand
        ? (bounds: DocumentDrawing['drawing_bounds']) =>
            finishDrawing(bounds, activeCommand.token)
        : undefined,
    onCancelDrawing:
      isDrawing && activeCommand
        ? () => cancelDrawing(activeCommand.token)
        : undefined,
    selectedRegionId,
    onSelectRegion: onSelectRegion ? selectRegion : undefined,
    onUpdateRegion:
      !disabled && cropDocumentId !== selectedDocumentId && onUpdateRegion
        ? updateRegion
        : undefined,
    onRemoveRegion:
      !disabled && cropDocumentId !== selectedDocumentId && onRemoveRegion
        ? removeRegion
        : undefined,
    onNaturalSize: onImageNaturalSize,
    drawingResetKey,
  }

  return {
    reports,
    selectedDocument,
    command,
    isReference,
    isDrawing,
    canDraw,
    canCrop: Boolean(
      selectedDocument && unitId && !isReference && !disabled && !cropDocumentId
    ),
    startDrawing,
    startDrawingForSelection,
    startDrawingForDocument,
    commandContext: activeCommand?.context,
    cancelDrawing,
    showRegion,
    openDocument: setSelectedDocumentId,
    startCrop,
    crop:
      cropDocumentId === selectedDocumentId &&
      selectedDocument &&
      unitId &&
      !disabled
        ? {
            sourceDocument: selectedDocument,
            unitId,
            onCancel: () => {
              currentRef.current.cropDocumentId = undefined
              setCropDocumentId(undefined)
            },
            onCropComplete: finishCrop,
          }
        : undefined,
    viewProps,
  }
}
